import React, { Fragment } from 'react';
import { get } from 'lodash';
import { Link } from 'react-router';
import Icon from 'chameleon/ui-stack/ui/base/icon';

/**
 * Troubleshooting Link
 */
const TROUBLESHOOTING_LINK =
  'https://apclassroom.clickhelp.co/articles/#!ap-classroom-user-guide-for-teachers-publication/qb-ldb-troubleshooting';

const SECURE_BROWSER_ARIA_LABBELEDBY = 'secure-browser-accessibility-details';

/**
 * Final exam durations
 */
export const getFullExamDuration = (isPilot) => (isPilot ? 60 : 105);
export const getPart1ExamDuration = (isPilot) => (isPilot ? 30 : 60);
export const getPart2ExamDuration = (isPilot) => (isPilot ? 30 : 45);

/**
 * Assessment group member part types
 */
export const PartType = {
  PARTIAL: {
    value: 'partial',
    duration: (isPilot) => getPart1ExamDuration(isPilot),
  },
  FULL: {
    value: 'full',
    duration: (isPilot) => getFullExamDuration(isPilot),
  },
};

export const getHelpTextISA = (assignItem) =>
  `Select classes or individual students to assign this ${assignItem}. ` +
  'To give students different time or formats, assign to them first, then assign to everyone else. ' +
  "Note: students can't be removed from a class assignment once any student starts taking it.";

export const allowHardTimer_timerDetailsText = `Assignments will be automatically submitted once the timer reaches
  zero unless the  ‘Allow submissions after timer expires’ checkbox is selected.`;

export const dontAllowHardTimer_timerHelpText = `Specify a time limit that students will see in a timer.
  When time's up, students are notified to submit but can continue working to support students with accommodations.
  To lock the quiz
  when the timer is up, schedule the due date and time to lock
  the quiz at the desired time.`;

export const casStartDate_helpText = `Schedule a start time and date for student access. Choose the current time to unlock now.`;

export const casDueDateEnabledLateSubmission_detailsText = `Late submissions are allowed for this assignment and this student will be able to access and submit this assignment after the due-date.
The allow late submission setting can only be updated at the assignment level.`;

export const casDueDateDisabledLateSubmission_detailsText = `Late submissions are not allowed for this assignment and in-progress assignments will be automatically submitted at this time.
The allow late submission setting can only be updated at the assignment level.`;

export const casHardTimerEnabled_detailsText = `This assignment has a strict time limit and will automatically submit when time expires.
The timer starts once a student begins the assignment and does not stop. Timer control settings can only be updated at the assignment level.`;

export const casHardTimerDisabled_detailsText = `This assignment does not have a strict time limit and students can continue working after time expires.
The timer starts once a student begins the assignment and does not stop. Timer control settings can only be updated at the assignment level.`;

export const allowLateSubmission_dueDate_helpText = (
  <Fragment>
    Schedule a due date for when this assignment should be completed. Any
    in-progress assignments will be automatically submitted at that time. These
    can be unsubmitted from the 'Assignment Overview' page.
    <br />
    Selecting the 'Allow Late Submissions' checkbox allows your students to
    continuing working on the assignment after the due date. You can set
    different start and due dates for individual students within this
    assignment.
  </Fragment>
);

export const allowHardTimer_timerHelpText = (
  <Fragment>
    The timer starts once a student begins the assignment and does not stop. By
    default, the assignment will automatically submit when the timer expires. If
    you unsubmit a timed assignment the timer will reset.
    <br />
    Enabling the “Allow submissions after timer expires” checkbox will allow
    them to continue working until they are ready to submit their assignment.
    <br />
    You can set different time limits for individual students in this assignment
    to support students with accommodations.
  </Fragment>
);

export const dontAllowHardTimer_timerDetailsText = `Students will be notified before they start this assignment to ensure they have enough time.
    Once the timer is up, students are notified but can continue working, unless you set the due date and time to enforce it.`;

export const STUDENT_RESULTS_LEGACY_OPTIONS = [
  {
    label: 'After Scoring',
    value: true,
  },
  {
    label: 'No',
    value: false,
  },
];

export const STUDENT_RESULTS_OPTIONS = [
  {
    label: 'Full Report',
    subLabel: 'Display all the performance and assessment information',
    value: 'full',
  },
  {
    label: 'Partial report without question information',
    subLabel: 'Students will not be able to access the question details',
    value: 'partial',
  },
  {
    label: 'No',
    subLabel: "Don't show any result information",
    value: 'no',
  },
];

const getStudentResultsOptions = (disablePartialResults) => {
  return disablePartialResults
    ? STUDENT_RESULTS_OPTIONS.map((option) => ({
        ...option,
        disabled: option.value === 'partial',
      }))
    : STUDENT_RESULTS_OPTIONS;
};

export const FORMAT = 'FORMAT';
export const STUDENT_RESULTS = 'STUDENT_RESULTS';
export const STUDENT_SCORING = 'STUDENT_SCORING';
export const START_DATE = 'START_DATE';
export const DUE_DATE = 'DUE_DATE';
export const TIMER = 'TIMER';
export const SCRAMBLE_QUESTIONS = 'SCRAMBLE_QUESTIONS';
export const LOCKDOWN_BROWSER = 'LOCKDOWN_BROWSER';

export const FIELD_META_MAP = {
  SECTIONS: {
    title: ({
      isAssign,
      hasPreassignedBehavior,
      individualStudentAssignmentFlag,
    }) =>
      isAssign
        ? hasPreassignedBehavior
          ? 'Unlock For'
          : 'Assign to Classes or Students'
        : individualStudentAssignmentFlag
          ? 'Update Settings For'
          : 'View Settings For',
    helpText: ({
      isAssign,
      individualStudentAssignmentFlag = false,
      isResourceForm,
      isSPP = false,
    }) => {
      if (!isAssign) {
        return null;
      }
      let sectionsHelpText;
      const assignItem = isResourceForm ? 'resource' : 'quiz';
      if (isSPP) {
        return `Select classes or individual students to assign this resource.
        To give students different time or formats, assign to them first, then assign to everyone else.
        Note: students can't be removed from a class assignment once any student starts taking it.`;
      }
      if (individualStudentAssignmentFlag) {
        sectionsHelpText = getHelpTextISA(assignItem);
      } else {
        sectionsHelpText = `Select one or more classes to assign this ${assignItem}.
        If you've already assigned this ${assignItem}, click 'View Assignment' to view or update it.
        To reassign a quiz, ensure  the first assignment is complete first.`;
      }

      return sectionsHelpText;
    },
    isHelpTextOpenedByDefault: ({
      featureFlags: { assignToPopoverDefaultOpened },
    }) => assignToPopoverDefaultOpened,
    placeholder: 'Select Classes or Students',
    detailsComponent: ({ isAssign, individualStudentAssignmentFlag }) =>
      !isAssign && individualStudentAssignmentFlag
        ? 'Update class or student assignments before students have access. ' +
          "Note: students can't be removed from a class assignment once any student starts taking it."
        : null,
  },
  [FORMAT]: {
    dataTestId: 'amf-format-option',
    title: () => 'Format',
    helpText: ({ isMCQPPC, featureFlags, isAssign }) => {
      if (isMCQPPC) {
        return `
          Progress Checks with MCQs can only be assigned online.
          This ensures students are scored automatically, and see
          rationales and feedback as soon as you make results available.
        `;
      }
      if (featureFlags.scrambleQuestionsPerStudent) {
        if (isAssign) {
          return `
            Choose whether students will take the quiz
            online or as a print test.
            Only the online format allows you to scramble questions for each student.
            (See “Scramble Questions?” under Advanced Settings.)
          `;
        }
        return `
          Choose whether students will take the quiz
          online or as a print test.
        `;
      }
      return `
        Choose whether students will take the quiz
        online or as a print test.
        If you need a print option after students take tests online, or to switch
        a quiz from paper to online, you can update format after you assign it.
      `;
    },
    placeholder: 'Select a Format',
    options: [
      {
        label: 'Online',
        value: false,
      },
      {
        label: 'Print',
        value: true,
      },
    ],
    detailsComponent: () => (
      <>
        You cannot assign questions in a different order for each student in the
        print format. Your selection for “Scramble Questions?” has been reset to
        “No.” To keep questions in a different order for each student, set the
        format to “Online.” You <strong>can</strong> assign questions in a
        different order for each <strong>class</strong> in the print format.
      </>
    ),
  },
  [STUDENT_RESULTS]: {
    dataTestId: 'amf-student-results-option',
    title: () => 'Students See Results',
    helpText: ({ featureFlags: { reviewModeFlag } }) =>
      !reviewModeFlag &&
      `Select "After scoring" to enable each student to see MCQ results after
    they complete their test and FRQ scores after you enter them.
    Select "No" to disable access to results until you enable it later.`,
    placeholder: 'Select When Students See Results',
    options: (reviewModeFlag, disablePartialResults = false) =>
      reviewModeFlag
        ? getStudentResultsOptions(disablePartialResults)
        : STUDENT_RESULTS_LEGACY_OPTIONS,
  },
  [STUDENT_SCORING]: {
    title: () => 'Students Score Their Own Work',
    helpText: () =>
      `This enables students to score their own responses to FRQs using AP scoring guidelines. You'll also be able to review and add your own scores.`,
    placeholder: 'Select an Option',
    options: [
      {
        label: 'No',
        value: false,
      },
      {
        label: 'Yes',
        value: true,
      },
    ],
    detailsComponent: () => (
      <div className="student-scores-details">
        <div className="line flex items-center mb-2 text-gray-600">
          <div className="icon-wrapper flex justify-center w-16 mr-2">
            <Icon id="graduate_hat" />
          </div>
          Results scored by students will include this icon.
        </div>
        <div className="line  flex items-center mb-2 text-gray-600">
          <div className="icon-wrapper flex justify-center w-16 mr-2">
            <Icon id="two_people" />
          </div>
          Results scored by students and teacher will include this icon.
        </div>
        <div className="line flex items-center mb-2 text-gray-600">
          Scores by students are not included on the Progress Dashboard
        </div>
      </div>
    ),
  },
  [START_DATE]: {
    dataTestId: 'amf-start-date-option',
    title: () => `Start Date`,
    helpText: ({
      isResourceForm,
      isSPP,
      featureFlags: { allowLateSubmission = false } = {},
      casModalConfig,
    } = {}) => {
      if (casModalConfig?.isCasModal) {
        return casStartDate_helpText;
      }

      const baseHelpText = 'Schedule a start time and date for student access.';
      const sppText = `
        Schedule a start time and date for students to see this as an assignment.
        Schedule a due date and time for this assignment to lock automatically.
      `;
      const oldStartDateHelpText = `
      ${baseHelpText} Choose the current time to unlock now.
      Schedule a due date and time for this quiz
      to lock automatically.`;
      const newStartDateHelpText = `
        ${baseHelpText} Choose the current time to unlock now.
        You can set different start and due dates for individual students within this assignment.
      `;

      if (isSPP) {
        return allowLateSubmission ? newStartDateHelpText : sppText;
      }

      if (isResourceForm) {
        return `
          ${baseHelpText} Video assignments must have a start date.
        `;
      }

      return allowLateSubmission ? newStartDateHelpText : oldStartDateHelpText;
    },
  },
  START_DATE_SESSION_2: {
    title: () => `Start Date (Session 2)`,
    helpText: () => {
      let startDateHelpText =
        'Schedule a start time and date for student access.';
      startDateHelpText = `${startDateHelpText} Choose the current time to unlock now.
        Schedule a due date and time for this quiz
        to lock automatically.`;
      return startDateHelpText;
    },
  },
  [DUE_DATE]: {
    dataTestId: 'amf-due-date-option',
    title: () => 'Due Date',
    helpText: ({
      isResourceForm,
      isSPP,
      featureFlags: { allowLateSubmission: allowLateSubmissionFF = false } = {},
      casModalConfig,
    }) => {
      const allowLateSubmission =
        casModalConfig?.featureFlags?.allowLateSubmission ||
        allowLateSubmissionFF;

      const sppText = `Schedule a start time and date for students to see this as an assignment.
      Schedule a due date and time for this assignment to lock automatically.`;
      const defaultText = `Schedule a start time and date for student access.
      Choose the current time to unlock now.
      Schedule a due date and time for this quiz to lock automatically.`;
      if (isSPP) {
        return allowLateSubmission
          ? allowLateSubmission_dueDate_helpText
          : sppText;
      }
      if (isResourceForm) {
        return defaultText;
      } else {
        return allowLateSubmission
          ? allowLateSubmission_dueDate_helpText
          : defaultText;
      }
    },
    detailsComponent: (props) => {
      const casModalConfig = props?.casModalConfig;

      if (casModalConfig?.isCasModal) {
        return casModalConfig.isCASLateSubmission
          ? casDueDateEnabledLateSubmission_detailsText
          : casDueDateDisabledLateSubmission_detailsText;
      }

      const allowLateSubmission = get(
        props || {},
        'allowLateSubmission',
        false,
      );
      const lateSubmissionText = `
        In-progress assignments will be automatically submitted at this time unless the 'Allow Late Submissions' checkbox is selected.
      `;
      const defaultText = `
        In-progress assignments will be automatically submitted at this time.
        You can unsubmit students' assignments from the 'Assignment Overview' page.
      `;
      return allowLateSubmission ? lateSubmissionText : defaultText;
    },
  },
  [TIMER]: {
    dateTestId: 'amf-timer-option',
    title: () => 'Timer',
    helpText: (props = {}) => {
      const { allowHardTimer = false, casModalConfig } = props || {};
      const isHardTimer =
        casModalConfig?.featureFlags?.allowHardTimer || allowHardTimer;

      if (isHardTimer) {
        return allowHardTimer_timerHelpText;
      }

      return dontAllowHardTimer_timerHelpText;
    },
    detailsComponent: (props = {}) => {
      const { casModalConfig } = props || {};

      if (casModalConfig?.isCasModal) {
        return casModalConfig.isCASHardTimer
          ? casHardTimerEnabled_detailsText
          : casHardTimerDisabled_detailsText;
      }

      const { allowHardTimer = false } = props || {};
      if (allowHardTimer) {
        return allowHardTimer_timerDetailsText;
      }

      return dontAllowHardTimer_timerDetailsText;
    },
  },
  CUSTOM_NAME: {
    dataTestId: 'amf-rename-option',
    isOptional: true,
    title: ({ isResourceForm }) =>
      `Custom ${isResourceForm ? 'Resource' : 'Assignment'} Name`,
    helpText: () => `Enter a unique title for this assignment that students
    will see, if you do not want to use the current title.`,
  },
  [SCRAMBLE_QUESTIONS]: {
    dataTestId: 'amf-scramble-questions-option',
    title: () => 'Scramble Questions?',
    helpText: () => `Each class will see the questions in a different order.
    This option is unavailable for Practice Exams.`,
    placeholder: 'Select an Option',
    options: [
      {
        label: 'Yes',
        value: true,
      },
      {
        label: 'No',
        value: false,
      },
    ],
  },
  // This object will be used when scramble_questions_per_student is ON
  SCRAMBLE_QUESTIONS_PER_STUDENT: {
    dataTestId: 'amf-scramble-questions-option',
    title: () => 'Scramble Questions?',
    helpText: () => (
      <Fragment>
        This setting cannot be changed once assigned. It is also unavailable for
        full-length Practice Exams.
        <br />
        <br />
        For all options described below, questions with a shared stimulus always
        remain in their original order within that question set.
        <br />
        <br />
        <strong>Different question order for each student</strong>: Only
        available for online quizzes. Question order will be randomized for all
        students. When Student Results are enabled, students will have the
        option to display their results in your original order or in the order
        the questions were administered.
        <br />
        <br />
        <strong>Different question order for each class</strong>: Available for
        print and online quizzes. Students in the same class section will see
        the same question order.
        <br />
        <br />
        <strong>No</strong>: Question order is the same for all students.
      </Fragment>
    ),
    placeholder: 'Select an Option',
    options: (isFormatPrint) =>
      isFormatPrint
        ? [
            {
              label: 'Different question order for each class',
              value: 'scramble_questions',
            },
            {
              label: 'No',
              value: false,
            },
          ]
        : [
            {
              label: 'Different question order for each student',
              value: 'scramble_questions_per_student',
            },
            {
              label: 'Different question order for each class',
              value: 'scramble_questions',
            },
            {
              label: 'No',
              value: false,
            },
          ],
    detailsComponent(selectedOption) {
      if (
        ['scramble_questions_per_student', 'scramble_questions'].includes(
          selectedOption.value,
        )
      ) {
        return `Progress Checks are designed with a specific question order in mind. Changing the order may limit insights about student understanding of content and skills.`;
      }

      return null;
    },
  },
  [LOCKDOWN_BROWSER]: {
    dataTestId: 'amf-lockdown-browser-option',
    title: () => 'Enable LockDown Browser?',
    helpText: () =>
      `Require students to use the LockDown Browser when taking this assignment.
        FRQs with recorded or uploaded responses are not supported by the browser, so assign these questions in print.`,
    placeholder: 'Select an Option',
    options: [
      {
        label: 'Yes',
        value: true,
      },
      {
        label: 'No',
        value: false,
      },
    ],
    detailsComponent: () => (
      <div aria-labelledby={SECURE_BROWSER_ARIA_LABBELEDBY}>
        <span className="text-gray-600">
          Before your assignment start date, ensure students&apos; computers or
          tablets have the&nbsp;
        </span>
        <Link
          target="_blank"
          rel="noopener noreferrer"
          to={`${FYM_FRONTEND_URL}/lockdown`}
        >
          LockDown Browser
        </Link>
        <span className="text-gray-600">
          &nbsp;installed and complete the readiness check on their &nbsp;AP
          Classroom assignment page to confirm it&apos;s working.
          Troubleshoot&nbsp;
        </span>
        <Link
          target="_blank"
          rel="noopener noreferrer"
          to={TROUBLESHOOTING_LINK}
        >
          here
        </Link>
        .
      </div>
    ),
    ariaLabelledBy: SECURE_BROWSER_ARIA_LABBELEDBY,
    ariaLabel: `Before your assignment start date, ensure students' computers or tablets have the LockDown Browser
    installed and complete the readiness check on their AP Classroom assignment page to confirm it's working.
    `,
  },
  IMMEDIATE_FEEDBACK: {
    dataTestId: 'advancedSettings-has_immediate_feedback',
    title: () => 'Immediate Feedback',
    helpText: () =>
      `If Immediate Feedback is ON students will be given the opportunity to check their answers as they take the assessment.
      Assessments given with Immediate Feedback = ON will not be included in the Content & Skills Performance report.`,
    options: [
      {
        label: 'On',
        value: true,
      },
      {
        label: 'Off',
        value: false,
      },
    ],
  },
  IMMEDIATE_FEEDBACK_ATTEMPTS: {
    dataTestId: 'advancedSettings-immediate_feedback_attempts',
    title: () => 'Limit number of attempts',
    helpText:
      () => `If Unlimited is selected, students will be able to check their answer to each question as many times as they want.
     If a number is selected, the student will be limited to that number of attempts before the Check Answer button is disabled for the question.`,
    options: [
      {
        label: 'Unlimited',
        value: '0',
      },
      {
        label: '1',
        value: '1',
      },
      {
        label: '2',
        value: '2',
      },
      {
        label: '3',
        value: '3',
      },
      {
        label: '4',
        value: '4',
      },
      {
        label: '5',
        value: '5',
      },
      {
        label: '6',
        value: '6',
      },
    ],
  },
};

/**
 * Form fields mapping for easier use.
 * */
export const FORM_FIELDS_MAP = {
  assignees: 'assignees',
  updateAssignees: 'update_assignees',
  allAssignees: 'all_assignees',
  hasDateAndTime: 'has_date_and_time',
  startsAt: 'starts_at',
  startsAtPart2: 'starts_at_part_2',
  dueAt: 'due_at',
  immediateStatus: 'status',
  printToggle: 'print_toggle',
  hasTimeLimit: 'has_time_limit',
  timer: 'timer',
  isAssessmentRenamed: 'is_assessment_renamed',
  scoreDisplay: 'score_display',
  hasScrambledOrder: 'scramble_questions',
  hasImmediateFeedback: 'immediate_feedback',
  allowStudentScoring: 'student_scoring',
  title: 'title',
  isSecure: 'is_secure',
  duration: 'duration',
  attestationAgreementChecked: 'attestation_agreement_checked',
  partType: 'part_type',
  assessmentGroup: 'assessment_group',
  students: 'students',
  testTakingLocation: 'test_taking_location',
};

export const ASSIGN_MANAGE_FORM_NAME = 'assignManageForm';

export const FORM_IDS_SEPARATOR = '__';
